import React, { useContext, useEffect, useState } from "react";
import Auth from "../../../utils/AuthUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import mpAPI from "../../../api/admin/mpApi";
import { jwtDecode } from "jwt-decode";
import { UserContext } from "../../../utils/UserContext";
import useNumericInput from "../../../hooks/useNumericInput";
import useAlphabeticInput from "../../../hooks/useAlphabeticInput";

function MyProfile() {
  const nameToDisplay = Auth.getUserDetails("name");
  useNumericInput();
  useAlphabeticInput();

  const { setUser } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [image, setImage] = useState("");
  const [file, setFile] = useState("");
  const [errors, setErrors] = useState({});

  // page load hote hi data get karna hai
  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      const res = await mpAPI.getProfile();

      if (res.code === 200) {
        const result = res.data;
        if (result) {
          setEmail(result.email);
          setPhone(result.mobile);
          setName(result.name);
          setUsername(result.username);
          setImage(result.image);
        }
      } else {
        showError("Failed to fetch user details");
      }
    } catch (err) {
      showError("Failed to fetch user details");
    }
  };

  // selected image handle
  const handleImageChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    const maxSize = 2 * 1024 * 1024;

    if (!allowedTypes.includes(selectedFile.type)) {
      showError("Only JPG, JPEG, PNG, and WEBP formats allowed");
      return;
    }

    if (selectedFile.size > maxSize) {
      showError("File size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
    };
    reader.readAsDataURL(selectedFile);

    setFile(selectedFile);
  };

  // remove selected image
  const handleRemove = () => {
    setImage(null);
    setFile(null);
    setRemoveImage(true);
  };

  const validate = () => {
    const phoneRegex = /^[6-9]\d{9}$/;

    const newErrors = {};
    if (!name) {
      newErrors.name = "Name is required";
      showError("Name is required");
    }

    if (phone && phone.length < 10) {
      newErrors.phone = "Enter Valid Phone Number";
      showError("Enter Valid Phone Number");
    } else if (!phoneRegex.test(phone)) {
      newErrors.phone = "Phone must be of 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //submit form
  const handleSubmit = async (e) => {
    setIsLoading(true);

    e.preventDefault();

    if (!validate()) {
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    formData.append("name", name.trim());
    formData.append("mobile", phone.trim());
    formData.append("removeImage", removeImage);

    try {
      const res = await mpAPI.updateProfile(formData);

      if (res.code === 200) {
        localStorage.setItem("token", res.data.token);
        const decoded = jwtDecode(res.data.token);

        setUser(decoded);

        showSuccess(res.message);
      } else {
        showError(res.message);
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      showError(err.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>My Profile</h4>
              <h6>
                Manage your personal information, account settings, and
                preferences.
              </h6>
            </div>
          </div>
          <div className="card">
            <form method="post" onSubmit={handleSubmit}>
              <div className="card-header">
                <h4>Hey {nameToDisplay}, this is your space ✨</h4>
              </div>
              <div className="card-body profile-body">
                <h5 className="mb-2">
                  <i className="ti ti-user text-primary me-1" />
                  Basic Information
                </h5>

                <div className="profile-pic-upload image-field">
                  <div className="profile-pic">
                    <img
                      src={
                        image || `${process.env.PUBLIC_URL}/img/user-dummy.jpg`
                      }
                      className="object-fit-contain h-100 rounded-1"
                      alt="user"
                    />
                    {image && (
                      <button
                        type="button"
                        className="close rounded-1"
                        onClick={handleRemove}
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    )}
                  </div>
                  <div className="mb-3">
                    <div className="image-upload mb-0 d-inline-flex">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleImageChange}
                        className="d-none"
                        id="profileInput"
                      />
                      <label
                        htmlFor="profileInput"
                        className="btn btn-primary fs-13 m-0"
                      >
                        {image ? "Change" : "Upload"} Image
                      </label>
                    </div>
                    <p className="mt-2">
                      Upload an image below 2 MB, Accepted File format JPG, PNG,
                      JPEG, WEBP
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control only-alphabets"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                      {errors.name && (
                        <span className="errors text-danger">
                          {errors.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Email<span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Phone Number<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control only-numeric"
                        maxlength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      {errors.phone && (
                        <span className="errors text-danger">
                          {errors.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Save Changes
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default MyProfile;
